import type { SWRConfiguration } from 'swr'
import useSWR, { mutate } from 'swr'
import { ApiError } from 'src/utils/error'
import { config } from '../utils/config'
import { hash } from '../utils/hash'
import type { AnyAsyncFn, AnyFn, OptionalArray } from '../utils/type'

export const getSWRKey =
  (prefix: string) =>
  (...args: any[]) =>
    args.filter(i => i).length ? [prefix, ...args] : null

const fnId = <T extends AnyFn>(fn: T) =>
  config.isProd || !fn.name ? hash(fn.toString()).toString(16) : fn.name

export const createKeyGetter = <T extends AnyFn>(fn: T) => {
  return fn.length ? getSWRKey(fnId(fn)) : () => fnId(fn)
}

export const globalSWRConfig: SWRConfiguration = {
  onErrorRetry(err, _key, _config, revalidate, { retryCount }) {
    // Never retry on TypeError.
    if (err instanceof TypeError) return

    // Never retry on ApiError.
    if (err instanceof ApiError) return

    // Only retry up to 10 times.
    if (retryCount >= 10) return

    // Retry after 5 seconds.
    setTimeout(() => revalidate({ retryCount }), 5000)
  },
}

export const createSWR = <Fetcher extends AnyAsyncFn>(
  fn: Fetcher,
  opt?: SWRConfiguration,
) => {
  type Data = Awaited<ReturnType<Fetcher>>

  const getKey = createKeyGetter(fn)

  const t = {
    // for display function name in devtools
    [fn.name]: (...args: OptionalArray<Parameters<Fetcher>>) => {
      const key = getKey(...args)
      return useSWR<Data>(key, ([prefix, ...args]) => fn(...args), { ...opt })
    },
  }
  return t[fn.name]
}

export const createMutator = <Fetcher extends AnyAsyncFn>(fn: Fetcher) => {
  const getKey = createKeyGetter(fn)

  return (
    args?: Parameters<Fetcher>,
    data?: any,
    shouldRevalidate?: boolean,
  ) => {
    const key = args ? getKey(...args) : null
    mutate(key, data, shouldRevalidate)
  }
}
